import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dice5, Save, RefreshCw, Power, PowerOff, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * CrapsConfigPanel — admin controls for the in-house Craps table.
 * Wraps GET/PUT /api/admin/craps/config.
 */
export const CrapsConfigPanel = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [edited, setEdited] = useState(null);
    const [defaults, setDefaults] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/craps/config`, { withCredentials: true });
            const { defaults: d, ...rest } = data;
            setCfg(rest); setEdited(rest); setDefaults(d);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Craps config');
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const dirty = JSON.stringify(cfg) !== JSON.stringify(edited);

    const save = async () => {
        if (!edited) return;
        setSaving(true);
        try {
            await axios.put(`${API}/admin/craps/config`, edited, { withCredentials: true });
            toast.success('Craps config saved');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    if (!edited) return <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="craps-config-loading">Loading Craps config…</div>;

    const set = (k, v) => setEdited((e) => ({ ...e, [k]: v }));

    return (
        <div className="card-dark p-5 space-y-5" data-testid="craps-config-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Dice5 className="w-5 h-5 text-[#39FF14]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Craps Config</h3>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} disabled={loading} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Reload
                    </button>
                    <button onClick={() => defaults && setEdited({ ...defaults })} className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1" data-testid="craps-config-defaults">
                        <RotateCcw className="w-3.5 h-3.5" />Defaults
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-3">
                <div className="flex items-center gap-2">
                    {edited.enabled ? <Power className="w-4 h-4 text-[#39FF14]" /> : <PowerOff className="w-4 h-4 text-[#FF3B30]" />}
                    <div>
                        <p className="text-sm font-bold text-white">Table {edited.enabled ? 'open' : 'closed'}</p>
                        <p className="text-[11px] text-[#A0AAB2]">When closed, players see a "table on break" screen. In-progress sessions still resolve.</p>
                    </div>
                </div>
                <Switch checked={edited.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="craps-config-enabled" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Min bet</Label>
                    <Input type="number" step="0.25" min="0.25" value={edited.min_bet} onChange={(e) => set('min_bet', Number(e.target.value))} className="mt-1" data-testid="craps-config-min-bet" /></div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Max bet</Label>
                    <Input type="number" step="1" min="1" value={edited.max_bet} onChange={(e) => set('max_bet', Number(e.target.value))} className="mt-1" data-testid="craps-config-max-bet" /></div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Max odds ×</Label>
                    <Input type="number" step="1" min="1" value={edited.max_odds_multiple} onChange={(e) => set('max_odds_multiple', Number(e.target.value))} className="mt-1" data-testid="craps-config-max-odds" /></div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">3-4-5× odds</Label>
                    <Switch checked={edited.use_345_odds} onCheckedChange={(v) => set('use_345_odds', v)} data-testid="craps-config-345" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Field · 2 pays</Label>
                    <select value={edited.field_2_multiplier} onChange={(e) => set('field_2_multiplier', Number(e.target.value))} className="w-full mt-1 rounded-md border border-white/10 bg-[#0e0e0e] text-white text-sm px-2 py-1.5" data-testid="craps-config-field2">
                        <option value={2}>2×</option><option value={3}>3×</option>
                    </select>
                </div>
                <div><Label className="text-[10px] uppercase text-[#A0AAB2]">Field · 12 pays</Label>
                    <select value={edited.field_12_multiplier} onChange={(e) => set('field_12_multiplier', Number(e.target.value))} className="w-full mt-1 rounded-md border border-white/10 bg-[#0e0e0e] text-white text-sm px-2 py-1.5" data-testid="craps-config-field12">
                        <option value={2}>2×</option><option value={3}>3×</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Sounds</Label>
                    <Switch checked={edited.sounds_enabled} onCheckedChange={(v) => set('sounds_enabled', v)} data-testid="craps-config-sounds" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0e0e0e] p-2.5">
                    <Label className="text-xs text-white">Animations</Label>
                    <Switch checked={edited.animations_enabled} onCheckedChange={(v) => set('animations_enabled', v)} data-testid="craps-config-animations" />
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={!dirty || saving} className="bg-[#39FF14] hover:bg-[#39FF14]/90 text-black font-black" data-testid="craps-config-save">
                    <Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                </Button>
            </div>
        </div>
    );
};

export default CrapsConfigPanel;
